import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import { dateTime, generatePassword, timeAgo } from '../lib/format';
import { useDocumentTitle, useLoad } from '../lib/hooks';
import type { ApiKey, ErrorEntry, Project, User, UserRole } from '../lib/types';
import { useAuth } from '../state/auth';
import { AppShell } from '../components/AppShell';
import { Icon } from '../components/Icon';
import { ConfirmDialog, CopyButton, Dialog, Empty, ErrorState, Field, Menu, PageLoading } from '../components/ui';
import { useToast } from '../components/toast';

const ROLE_LABEL: Record<UserRole, string> = { admin: 'Admin', team: 'Team', client: 'Client' };

// ── People ─────────────────────────────────────────────────────────────────
interface UserRow extends User {
  projects: { id: string; name: string; role: string }[];
}

function NewUserDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { meta } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<UserRole>('team');
  const [password, setPassword] = useState(() => generatePassword());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<User | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<{ user: User }>('/api/users', { name, email, role, password, ...(username.trim() ? { username: username.trim() } : {}) });
      onCreated();
      setDone(created.user);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    // The username comes back from the server: it is tidied there (lower case, no spaces).
    const details = `Sign in: ${meta?.appUrl ?? location.origin}/login\nEmail: ${email}${done.username ? `\nUsername: ${done.username}` : ''}\nPassword: ${password}`;
    return (
      <Dialog title={`${name} can now sign in`} onClose={onClose} footer={<button className="btn btn-primary" onClick={onClose}>Done</button>}>
        <p className="muted">Send them these details. The password is not shown again; they can change it under Account.</p>
        <pre className="codeblock" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {details}
        </pre>
        <CopyButton text={details} label="Copy details" />
      </Dialog>
    );
  }

  return (
    <Dialog title="New account" description="Team members and admins can open every project. Add clients from a project's Access tab instead." onClose={onClose}>
      <form className="stack" style={{ gap: 16 }} onSubmit={submit}>
        <Field label="Name">{(id) => <input id={id} className="input" value={name} onChange={(e) => setName(e.target.value)} required />}</Field>
        <Field label="Email">{(id) => <input id={id} className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />}</Field>
        <Field label="Username" hint="Optional. They can sign in with it instead of their email.">
          {(id) => <input id={id} className="input" value={username} onChange={(e) => setUsername(e.target.value)} maxLength={40} autoCapitalize="none" spellCheck={false} />}
        </Field>
        <Field label="Role">
          {(id) => (
            <select id={id} className="select" value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
              <option value="team">Team: all projects, can create and manage them</option>
              <option value="admin">Admin: team access plus people, API keys and debug</option>
              <option value="client">Client: only projects they are added to</option>
            </select>
          )}
        </Field>
        <Field label="Password">
          {(id) => (
            <div className="input-row">
              <input id={id} className="input mono" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required autoComplete="off" />
              <button type="button" className="btn btn-secondary" onClick={() => setPassword(generatePassword())}>
                Generate
              </button>
            </div>
          )}
        </Field>
        {error && (
          <div className="notice notice-error" role="alert">
            {error}
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={busy}>
            Create account
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose }: { user: User; onClose: () => void }) {
  const toast = useToast();
  const [password, setPassword] = useState(() => generatePassword());
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/api/users/${user.id}/password`, { password });
      setSaved(true);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={`New password for ${user.name}`}
      description={saved ? 'Saved. Send it to them now; it is not shown again.' : 'They will be signed out everywhere and must use the new password.'}
      onClose={onClose}
      footer={
        saved ? (
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={save} disabled={busy || password.length < 8}>
              Set password
            </button>
          </>
        )
      }
    >
      <div className="input-row">
        <input className="input mono" aria-label="New password" value={password} onChange={(e) => setPassword(e.target.value)} readOnly={saved} />
        {saved ? (
          <CopyButton text={password} className="btn btn-secondary" />
        ) : (
          <button type="button" className="btn btn-secondary" onClick={() => setPassword(generatePassword())}>
            Generate
          </button>
        )}
      </div>
    </Dialog>
  );
}

export function UsersPage() {
  useDocumentTitle('People');
  const { isAdmin, user: me } = useAuth();
  const toast = useToast();
  const { data, loading, error, reload } = useLoad(() => api.get<{ users: UserRow[] }>('/api/users'), []);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<User | null>(null);

  if (!isAdmin) return <Navigate to="/" replace />;
  if (loading && !data) return <PageLoading />;
  if (error) {
    return (
      <AppShell crumbs={[{ label: 'People' }]}>
        <ErrorState message={error} onRetry={() => reload()} />
      </AppShell>
    );
  }

  const update = async (user: User, body: Record<string, unknown>) => {
    try {
      await api.patch(`/api/users/${user.id}`, body);
      await reload(true);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <AppShell crumbs={[{ label: 'People' }]}>
      <div className="page-head">
        <div>
          <h1>People</h1>
          <p>Everyone who can sign in to Revisions, and what they can reach.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          <Icon name="plus" /> New account
        </button>
      </div>
      <div className="card table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Role</th>
              <th>Projects</th>
              <th>Signs in with</th>
              <th>Last seen</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data!.users.map((user) => (
              <tr key={user.id} style={user.isActive ? undefined : { opacity: 0.5 }}>
                <td>
                  <div style={{ fontWeight: 500 }}>
                    {user.name} {user.id === me?.id && <span className="muted">(you)</span>}
                  </div>
                  <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
                    {user.email}
                    {user.username ? ` · ${user.username}` : ''}
                  </div>
                </td>
                <td>
                  <select className="select select-inline" aria-label={`Role for ${user.name}`} value={user.role} disabled={user.id === me?.id} onChange={(e) => void update(user, { role: e.target.value })} style={{ border: '1px solid var(--border-medium)' }}>
                    {(Object.keys(ROLE_LABEL) as UserRole[]).map((role) => (
                      <option key={role} value={role}>
                        {ROLE_LABEL[role]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="muted">{user.role === 'client' ? user.projects.map((p) => p.name).join(', ') || 'None' : 'All projects'}</td>
                <td className="muted">{!user.isActive ? 'Disabled' : user.hasPassword ? 'Own password' : 'Project password'}</td>
                <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                  {user.lastLoginAt ? timeAgo(user.lastLoginAt) : 'Never'}
                </td>
                <td>
                  <div className="row" style={{ justifyContent: 'flex-end' }}>
                    <Menu
                      label={`Actions for ${user.name}`}
                      trigger={<Icon name="more" />}
                      items={[
                        { label: 'Set a new password', icon: 'key', onSelect: () => setResetting(user) },
                        { label: user.isActive ? 'Disable account' : 'Enable account', icon: user.isActive ? 'lock' : 'check', danger: user.isActive, disabled: user.id === me?.id, onSelect: () => void update(user, { isActive: !user.isActive }) },
                      ]}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {creating && <NewUserDialog onClose={() => setCreating(false)} onCreated={() => void reload(true)} />}
      {resetting && <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />}
    </AppShell>
  );
}

// ── API keys ───────────────────────────────────────────────────────────────
function NewKeyDialog({ projects, onClose, onCreated }: { projects: Project[]; onClose: () => void; onCreated: () => void }) {
  const { meta } = useAuth();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'read' | 'write'>('write');
  const [projectId, setProjectId] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ secret: string }>('/api/api-keys', { name, scope, projectId: projectId || null });
      setSecret(result.secret);
      onCreated();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (secret) {
    const env = `REVISIONS_API_KEY=${secret}`;
    const mcp = `claude mcp add --transport http revisions ${meta?.appUrl ?? location.origin}/mcp --header "Authorization: Bearer ${secret}"`;
    return (
      <Dialog title="Copy your API key now" description="This is the only time the full key is shown. Store it somewhere safe, like a password manager or a .env file that is not committed." onClose={onClose} wide footer={<button className="btn btn-primary" onClick={onClose}>I have copied it</button>}>
        <div className="stack" style={{ gap: 8 }}>
          <div className="row-between">
            <span className="field-label">Key</span>
            <CopyButton text={secret} />
          </div>
          <pre className="codeblock" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {env}
          </pre>
        </div>
        <div className="stack" style={{ gap: 8 }}>
          <div className="row-between">
            <span className="field-label">Connect Claude Code</span>
            <CopyButton text={mcp} />
          </div>
          <pre className="codeblock" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {mcp}
          </pre>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title="New API key" description="For Claude Code, Codex, scripts and automations." onClose={onClose}>
      <form className="stack" style={{ gap: 16 }} onSubmit={submit}>
        <Field label="Name" hint={'Shown next to anything this key does, e.g. "Claude Code (API)".'}>
          {(id) => <input id={id} className="input" placeholder="Claude Code" value={name} onChange={(e) => setName(e.target.value)} required />}
        </Field>
        <Field label="Can">
          {(id) => (
            <select id={id} className="select" value={scope} onChange={(e) => setScope(e.target.value as 'read' | 'write')}>
              <option value="write">Read feedback, reply, resolve and upload new versions</option>
              <option value="read">Read feedback only</option>
            </select>
          )}
        </Field>
        <Field label="Limited to">
          {(id) => (
            <select id={id} className="select" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">All projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        {error && (
          <div className="notice notice-error" role="alert">
            {error}
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={busy}>
            Create key
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function ApiKeysPage() {
  useDocumentTitle('API keys');
  const { isStaff } = useAuth();
  const toast = useToast();
  const { data, loading, error, reload } = useLoad(async () => {
    const [keys, projects] = await Promise.all([api.get<{ keys: ApiKey[] }>('/api/api-keys'), api.get<{ projects: Project[] }>('/api/projects')]);
    return { keys: keys.keys, projects: projects.projects };
  }, []);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);

  if (!isStaff) return <Navigate to="/" replace />;
  if (loading && !data) return <PageLoading />;
  if (error) {
    return (
      <AppShell crumbs={[{ label: 'API keys' }]}>
        <ErrorState message={error} onRetry={() => reload()} />
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={[{ label: 'API keys' }]}>
      <div className="page-head">
        <div>
          <h1>API keys</h1>
          <p>
            Let AI agents and scripts read feedback as data and act on it. Reference:{' '}
            <a href="/api/v1/openapi.json" target="_blank" rel="noreferrer">
              OpenAPI
            </a>
            .
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          <Icon name="plus" /> New key
        </button>
      </div>
      {data!.keys.length === 0 ? (
        <Empty
          icon="key"
          title="No API keys yet"
          action={
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" /> New key
            </button>
          }
        >
          Create one to connect Claude Code over MCP, or to pull FEEDBACK.md into a repo from a script.
        </Empty>
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Can</th>
                <th>Limited to</th>
                <th>Last used</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data!.keys.map((key) => (
                <tr key={key.id}>
                  <td style={{ fontWeight: 500 }}>{key.name}</td>
                  <td className="mono muted">{key.prefix}…</td>
                  <td>
                    <span className={`badge ${key.scope === 'write' ? 'badge-progress' : ''}`}>{key.scope === 'write' ? 'Read and write' : 'Read only'}</span>
                  </td>
                  <td className="muted">{key.projectName || 'All projects'}</td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {key.lastUsedAt ? timeAgo(key.lastUsedAt) : 'Never'}
                  </td>
                  <td>
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      <button className="btn btn-danger btn-sm" onClick={() => setRevoking(key)}>
                        Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <NewKeyDialog projects={data!.projects} onClose={() => setCreating(false)} onCreated={() => void reload(true)} />}
      {revoking && (
        <ConfirmDialog
          title={`Revoke "${revoking.name}"?`}
          message="Anything using this key stops working immediately. This cannot be undone."
          confirmLabel="Revoke key"
          danger
          onClose={() => setRevoking(null)}
          onConfirm={async () => {
            try {
              await api.del(`/api/api-keys/${revoking.id}`);
              await reload(true);
            } catch (err) {
              toast.error(errorMessage(err));
            }
          }}
        />
      )}
    </AppShell>
  );
}

// ── Debug ──────────────────────────────────────────────────────────────────
interface Status {
  version: string;
  environment: string;
  node: string;
  uptimeSeconds: number;
  appUrl: string;
  proxyUrl: string;
  proxyMode: string;
  storage: string;
  database: string;
  emailEnabled: boolean;
  webhooks: number;
  liveConnections: number;
  counts: Record<string, number>;
}

export function DebugPage() {
  useDocumentTitle('Debug');
  const { isAdmin } = useAuth();
  const toast = useToast();
  const { data, loading, error, reload } = useLoad(async () => {
    const [errors, status] = await Promise.all([api.get<{ errors: ErrorEntry[] }>('/api/debug/errors?limit=200'), api.get<{ status: Status }>('/api/debug/status')]);
    return { errors: errors.errors, status: status.status };
  }, []);
  const [open, setOpen] = useState<number | null>(null);

  if (!isAdmin) return <Navigate to="/" replace />;
  if (loading && !data) return <PageLoading />;
  if (error || !data) {
    return (
      <AppShell crumbs={[{ label: 'Debug' }]}>
        <ErrorState message={error || 'No data'} onRetry={() => reload()} />
      </AppShell>
    );
  }

  const { status, errors } = data;
  return (
    <AppShell crumbs={[{ label: 'Debug' }]}>
      <div className="page-head">
        <div>
          <h1>Debug</h1>
          <p>Live server status and every error the server or a browser has reported. The debug loop reads the same log.</p>
        </div>
        <div className="row">
          <button className="btn btn-secondary" onClick={() => reload(true)}>
            <Icon name="refresh" /> Refresh
          </button>
          <button
            className="btn btn-danger"
            disabled={errors.length === 0}
            onClick={async () => {
              try {
                await api.del('/api/debug/errors');
                await reload(true);
                toast.show('Error log cleared');
              } catch (err) {
                toast.error(errorMessage(err));
              }
            }}
          >
            Clear log
          </button>
        </div>
      </div>

      <div className="card card-pad">
        <dl className="kv">
          <dt>Version</dt>
          <dd>
            {status.version} · {status.environment} · Node {status.node} · up {Math.round(status.uptimeSeconds / 60)} min
          </dd>
          <dt>App address</dt>
          <dd className="mono">{status.appUrl}</dd>
          <dt>Review proxy</dt>
          <dd className="mono">
            {status.proxyUrl} ({status.proxyMode})
          </dd>
          <dt>File storage</dt>
          <dd>{status.storage}</dd>
          <dt>Database</dt>
          <dd className="mono truncate">{status.database}</dd>
          <dt>Email</dt>
          <dd>{status.emailEnabled ? 'Enabled' : 'Off: invitations show a link to copy instead'}</dd>
          <dt>Data</dt>
          <dd>
            {status.counts.projects} projects · {status.counts.items} items · {status.counts.comments} comments · {status.counts.users} people · {status.liveConnections} live connections
          </dd>
        </dl>
      </div>

      <div className="section">
        <h2>Error log</h2>
        {errors.length === 0 ? (
          <Empty icon="check" title="No errors recorded">
            Server crashes and browser errors will be listed here with their stack traces.
          </Empty>
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            {errors.map((entry) => (
              <div key={entry.id} className="card" style={{ padding: 12 }}>
                <button
                  type="button"
                  className="row-between"
                  style={{ width: '100%', border: 0, background: 'none', padding: 0, textAlign: 'left', color: 'inherit' }}
                  aria-expanded={open === entry.id}
                  onClick={() => setOpen(open === entry.id ? null : entry.id)}
                >
                  <span className="row" style={{ minWidth: 0 }}>
                    <span className={`badge ${entry.source === 'server' ? 'badge-danger' : 'badge-progress'}`}>{entry.source}</span>
                    {entry.count > 1 && <span className="badge">×{entry.count}</span>}
                    <span className="truncate" style={{ fontWeight: 500 }}>
                      {entry.message}
                    </span>
                  </span>
                  <span className="muted" style={{ whiteSpace: 'nowrap', fontSize: 'var(--fs-xs)' }}>
                    {dateTime(entry.lastAt)}
                  </span>
                </button>
                {open === entry.id && (
                  <pre className="codeblock" style={{ marginTop: 12, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {[entry.scope && `scope: ${entry.scope}`, entry.stack, entry.context && JSON.stringify(entry.context, null, 2)].filter(Boolean).join('\n\n') || 'No further detail.'}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

// ── Account ────────────────────────────────────────────────────────────────
export function AccountPage() {
  useDocumentTitle('Account');
  const { user } = useAuth();
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/change-password', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      toast.show('Password changed. Other devices have been signed out.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell crumbs={[{ label: 'Account' }]} width="narrow">
      <div className="page-head">
        <div>
          <h1>Account</h1>
          <p>
            {user.name} · {user.email} · {ROLE_LABEL[user.role]}
          </p>
        </div>
      </div>
      {user.hasPassword ? (
        <form className="card card-pad stack" style={{ gap: 16, maxWidth: 420 }} onSubmit={submit}>
          <h3 className="card-title">Change password</h3>
          <Field label="Current password">
            {(id) => <input id={id} className="input" type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />}
          </Field>
          <Field label="New password" hint="At least 8 characters.">
            {(id) => <input id={id} className="input" type="password" autoComplete="new-password" minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />}
          </Field>
          {error && (
            <div className="notice notice-error" role="alert">
              {error}
            </div>
          )}
          <button className="btn btn-primary" disabled={busy} style={{ alignSelf: 'flex-start' }}>
            Change password
          </button>
        </form>
      ) : (
        <div className="notice">You signed in with a project password, so there is no personal password to change. Ask MK-Way if you would like your own account.</div>
      )}
    </AppShell>
  );
}
